import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Modal,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  adminEditCleared,
  adminEditSaved,
  adminSaveReset,
  type ApprovalStatus,
  type FactEdit,
  type NewsEditSource,
} from '../store/slices/admin-slice';
import { selectAdminSaveState } from '../store/selectors/admin-selectors';
import { saveAdminEditThunk } from '../store/thunks/admin-thunks';
import { FactItemPreview } from './FactItemPreview';

interface FactEditDrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly recordId: string | undefined;
  readonly initialValues: FactEdit;
  readonly hasExistingEdit: boolean;
  /**
   * Target record's `day_of_month` PK on the Facts table (`MM-DD`).
   * Required for the server-side save round-trip
   * (`content-ingestion-pipeline` Sub-R5.1).
   */
  readonly factDayOfMonth: string | undefined;
  /**
   * Display date (`YYYY-MM-DD`) — used to refetch the day cache
   * after a successful save.
   */
  readonly refetchDate: string | undefined;
  /** Current `metadata.version` of the record. */
  readonly recordVersion: number | undefined;
}

const STATUS_OPTIONS: ReadonlyArray<{ readonly value: ApprovalStatus; readonly label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const EMPTY_SOURCE: NewsEditSource = { name: '', url: '' };

/**
 * `<FactEditDrawer>` — admin-mode-only edit modal for a single
 * fact-of-the-day record. Mirrors `<NewsEditDrawer>` (live preview
 * panel + form panel) but with the fact-specific shape — no title,
 * just body + image + sources + status.
 */
export function FactEditDrawer({
  open,
  onClose,
  recordId,
  initialValues,
  hasExistingEdit,
  factDayOfMonth,
  refetchDate,
  recordVersion,
}: FactEditDrawerProps): JSX.Element {
  const dispatch = useAppDispatch();
  const [values, setValues] = useState<FactEdit>(initialValues);
  const saveState = useAppSelector((state) => selectAdminSaveState(state, recordId));
  const isSaving = saveState.status === 'saving';
  const canSave =
    Boolean(recordId) && Boolean(factDayOfMonth) && Boolean(refetchDate)
    && typeof recordVersion === 'number';
  useEffect(() => {
    if (open) {
      setValues({
        ...initialValues,
        sources: initialValues.sources.length > 0 ? initialValues.sources : [EMPTY_SOURCE],
      });
      if (recordId) dispatch(adminSaveReset({ id: recordId }));
    }
  }, [open, initialValues, recordId, dispatch]);
  const setField =
    <K extends keyof FactEdit>(key: K) =>
    (value: FactEdit[K]): void =>
      setValues((prev) => ({ ...prev, [key]: value }));
  const setSourceField = (index: number, key: keyof NewsEditSource) =>
    (event: React.ChangeEvent<HTMLInputElement>): void =>
      setValues((prev) => {
        const next = [...prev.sources];
        next[index] = { ...next[index], [key]: event.target.value };
        return { ...prev, sources: next };
      });
  const addSource = (): void =>
    setValues((prev) => ({ ...prev, sources: [...prev.sources, EMPTY_SOURCE] }));
  const removeSource = (index: number): void =>
    setValues((prev) => {
      const next = prev.sources.filter((_, i) => i !== index);
      return { ...prev, sources: next.length > 0 ? next : [EMPTY_SOURCE] };
    });
  const handleSave = (): void => {
    if (!recordId || !canSave || !factDayOfMonth || !refetchDate
      || typeof recordVersion !== 'number') return;
    const cleanSources = values.sources.filter((s) => s.name.trim() || s.url.trim());
    dispatch(
      adminEditSaved({ id: recordId, edit: { ...values, kind: 'fact', sources: cleanSources } }),
    );
    void dispatch(
      saveAdminEditThunk({
        tableName: 'facts',
        uuid: recordId,
        date: factDayOfMonth,
        patch: buildFactPatch(values, cleanSources),
        expectedVersion: recordVersion,
        refetchDate,
      }),
    );
  };
  const handleReset = (): void => {
    if (!recordId) return;
    dispatch(adminEditCleared({ id: recordId }));
    onClose();
  };
  const previewSources = useMemo(
    () => values.sources.filter((s) => s.name.trim() || s.url.trim()),
    [values.sources],
  );
  return (
    <Modal
      open={open}
      onClose={onClose}
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.85)' } } }}
      aria-labelledby="fact-edit-modal-title"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95vw', md: 'min(1080px, 92vw)' },
          maxHeight: '92vh',
          outline: 'none',
        }}
      >
        <Paper
          elevation={24}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            borderRadius: 2,
            overflow: 'hidden',
            maxHeight: '92vh',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              p: { xs: 2, md: 3 },
              borderRight: { md: '1px solid' },
              borderBottom: { xs: '1px solid', md: 'none' },
              borderColor: { xs: 'divider', md: 'divider' },
              overflow: 'auto',
              backgroundColor: 'background.paper',
            }}
          >
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Live preview
            </Typography>
            <FactItemPreview values={values} sources={previewSources} />
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              p: { xs: 2, md: 3 },
              overflow: 'auto',
              backgroundColor: 'background.default',
            }}
          >
            <Stack spacing={2}>
              <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography
                  id="fact-edit-modal-title"
                  variant="h2"
                  component="h2"
                  sx={{ fontSize: '1.25rem' }}
                >
                  Edit fact of the day
                </Typography>
                <IconButton onClick={onClose} aria-label="Close edit dialog" size="small">
                  <CloseIcon />
                </IconButton>
              </Stack>
              {saveState.status === 'saved' && (
                <Alert severity="success">Saved. Refetching content…</Alert>
              )}
              {saveState.status === 'failed' && (
                <Alert severity="error">Save failed: {saveState.error ?? 'unknown error'}</Alert>
              )}
              {!canSave && recordId && (
                <Alert severity="warning">
                  Save unavailable — record is missing a version or date on the wire.
                </Alert>
              )}
              <TextField
                select
                label="Approval status"
                value={values.status}
                onChange={(e) => setField('status')(e.target.value as ApprovalStatus)}
                size="small"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Body"
                fullWidth
                multiline
                minRows={3}
                maxRows={8}
                value={values.body}
                onChange={(e) => setField('body')(e.target.value)}
              />
              <TextField
                label="Image URL"
                fullWidth
                value={values.image}
                onChange={(e) => setField('image')(e.target.value)}
                placeholder="https://"
              />
              {values.image && (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                    backgroundColor: 'background.paper',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Image preview
                  </Typography>
                  <Box
                    component="img"
                    src={values.image}
                    alt="Image preview"
                    sx={{
                      width: '100%',
                      maxHeight: 160,
                      objectFit: 'contain',
                      borderRadius: 1,
                    }}
                  />
                </Box>
              )}
              <Stack spacing={1}>
                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2">Sources</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={addSource}>
                    Add source
                  </Button>
                </Stack>
                {values.sources.map((source, index) => (
                  <Stack key={index} direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                    <TextField
                      label="Name"
                      size="small"
                      value={source.name}
                      onChange={setSourceField(index, 'name')}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="URL"
                      size="small"
                      value={source.url}
                      onChange={setSourceField(index, 'url')}
                      sx={{ flex: 2 }}
                      placeholder="https://"
                    />
                    <IconButton
                      size="small"
                      onClick={() => removeSource(index)}
                      aria-label={`Remove source ${index + 1}`}
                      sx={{ mt: 0.5 }}
                      disabled={values.sources.length === 1 && !source.name && !source.url}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
              <Box sx={{ flexGrow: 1 }} />
              <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', pt: 1 }}>
                {hasExistingEdit && (
                  <Button color="inherit" onClick={handleReset} disabled={isSaving}>
                    Reset to original
                  </Button>
                )}
                <Button variant="outlined" onClick={onClose} disabled={isSaving}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={!canSave || isSaving}
                  startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                  {isSaving ? 'Saving…' : 'Save'}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
}

/**
 * Project a `FactEdit` + cleaned source list onto the JSON envelope
 * patch. Facts have no title — just body + image + source
 * annotations. `status` transitions belong to ApprovalManager
 * (approve / reject) and are intentionally omitted here.
 */
function buildFactPatch(
  values: FactEdit,
  cleanSources: ReadonlyArray<NewsEditSource>,
): Record<string, unknown> {
  const patch: Record<string, unknown> = { body: values.body, image: values.image };
  const primary = cleanSources[0];
  if (primary) {
    patch.source = primary.name;
    patch.sourceUrl = primary.url;
  }
  const corroborating = cleanSources.slice(1);
  if (corroborating.length > 0) {
    patch.corroboratingSources = corroborating.map((s) => ({ source: s.name, url: s.url }));
  }
  return patch;
}
